import type { Ref } from "vue";
import { computed, watch } from "vue";

import type { ActionMenuConfig } from "@/components/ActionMenu/useActionMenu";
import { useActionMenu } from "@/components/ActionMenu/useActionMenu";
import type { Card } from "@/model/card";
import variables from "@/model/variable.module.scss";
import { useBoardStore } from "@/store/board";
import { useDraggingStore } from "@/store/dragging";
import { useLinkStore } from "@/store/link";

interface Props {
  referenceEl: Ref<HTMLElement | null>;
  card: Card;
  disabled: Ref<Readonly<boolean>>;
}

export function useStickyActionMenu({ referenceEl, card, disabled }: Props) {
  const open = computed(
    () =>
      useBoardStore().activeCardId === card.id &&
      !useDraggingStore().dragging[card.id] &&
      !useLinkStore().linking.from,
  );

  const size = parseInt(variables.stickySize.replace("px", ""));
  const config = computed<ActionMenuConfig>(() => {
    const isPinned = useLinkStore().isPinned(card.id);
    return {
      placement: "top",
      offset: ({ reference }) => calculateOffset(reference.height, isPinned, size),
    };
  });

  const { isOpen, updateMenu, updateRef } = useActionMenu({ referenceEl, open, disabled, config });

  // update the action menu position when the card type, or priority changes
  watch([() => card.type, () => card.priority], updateMenu);

  return { isOpen, updateRef };
}

function calculateOffset(
  referenceHeight: number,
  isPinned: boolean,
  size: number,
): [number, number] {
  const multiplier = referenceHeight > 0 ? size / referenceHeight : 1;
  const offsetY = isPinned ? 11 : 5;

  return [0, offsetY / multiplier];
}
